import {useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {Button} from '../../../../../../../ui/atoms/button';
import {LockIcon} from '../../../../../../../ui/atoms/icons';
import {ToggleSwitch} from '../../../../../../../ui/atoms/toggle-switch';
import {Modal} from '../../../../../../../ui/molecules/modal';
import FreeTierModal from '../../../../../../../ui/organisms/free-tier/free-tier-modal';
import {illustrationList} from '../../../../../../../ui/assets/illustrations';
import {useStoreContext} from '../../../../../../../store/store-context';
import {
  DEFAULT_GOAL_DISPLAY_NAMES,
  GoalDisplayNameField,
  isDisplayNameDirty,
  normalizeDisplayName,
} from './configure-goals.utils';
import {useConfigureGoals} from './use-configure-goals';
import {
  ConfirmationBody,
  ConfirmationDialog,
  ConfirmationFooter,
  ConfirmationHeader,
  DisplayNameControl,
  FeatureRow,
  GoalSettingsBody,
  GoalSettingsIntro,
  GoalSettingsPage,
  NameActions,
  ResetNameButton,
  SettingCopy,
  SettingDescription,
  SettingRow,
  SettingsCard,
  SettingsCardTitle,
  SettingsInput,
  SettingsToggleWrap,
  SettingTitleLine,
} from './admin-goal-framework.styles';

type FreeTierFeature =
  | 'objectiveName'
  | 'kpi'
  | 'goalAutoEnd'
  | 'allowGoalUpdate';

interface DisplayNameRowProps {
  description: string;
  field: GoalDisplayNameField;
  isLocked: boolean;
  isSaving: boolean;
  limited: boolean;
  onCancel: () => void;
  onChange: (value: string) => void;
  onLockedAction: () => void;
  onReset: () => void;
  onSave: () => void;
  savedValue: string;
  title: string;
  value: string;
}

const DisplayNameRow = ({
  description,
  field,
  isLocked,
  isSaving,
  limited,
  onCancel,
  onChange,
  onLockedAction,
  onReset,
  onSave,
  savedValue,
  title,
  value,
}: DisplayNameRowProps) => {
  const dirty = isDisplayNameDirty(value, savedValue);
  const canSave = Boolean(normalizeDisplayName(value)) && dirty && !limited;
  const customValue =
    normalizeDisplayName(savedValue) !== DEFAULT_GOAL_DISPLAY_NAMES[field];

  return (
    <SettingRow>
      <SettingCopy>
        <SettingTitleLine>
          {isLocked && <LockIcon />}
          {title}
        </SettingTitleLine>
        <SettingDescription>{description}</SettingDescription>
      </SettingCopy>
      <DisplayNameControl>
        <SettingsInput
          aria-label={title}
          disabled={limited}
          readOnly={isLocked}
          value={value}
          onClick={isLocked ? onLockedAction : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
        {dirty ? (
          <NameActions>
            <Button type="button" kind="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canSave || isSaving}
              isLoading={isSaving}
              onClick={onSave}>
              Save
            </Button>
          </NameActions>
        ) : (
          customValue && (
            <ResetNameButton type="button" onClick={onReset}>
              reset to {DEFAULT_GOAL_DISPLAY_NAMES[field]}
            </ResetNameButton>
          )
        )}
      </DisplayNameControl>
    </SettingRow>
  );
};

interface ToggleRowProps {
  checked: boolean;
  description: string;
  disabled?: boolean;
  locked?: boolean;
  onChange: () => void;
  switchId: number;
  title: string;
}

const ToggleRow = ({
  checked,
  description,
  disabled,
  locked,
  onChange,
  switchId,
  title,
}: ToggleRowProps) => (
  <FeatureRow>
    <SettingCopy>
      <SettingTitleLine>
        {locked && <LockIcon />}
        {title}
      </SettingTitleLine>
      <SettingDescription>{description}</SettingDescription>
    </SettingCopy>
    <SettingsToggleWrap>
      <ToggleSwitch
        aria-label={title}
        checked={checked}
        disabled={disabled}
        switchId={switchId}
        onChange={onChange}
      />
    </SettingsToggleWrap>
  </FeatureRow>
);

export const AdminGoalFramework = observer(() => {
  const {
    authStore: {auth, featureLimitStatus},
  } = useStoreContext();
  const {
    alignmentEnabled,
    autoEndEnabled,
    busyFeature,
    cancelDisplayName,
    draftNames,
    kpiEnabled,
    memberEditEnabled,
    savedNames,
    saveDisplayName,
    savingName,
    setAlignment,
    setAutoEnd,
    setKpi,
    setMemberEdit,
    stageDefaultDisplayName,
    updateDraftName,
  } = useConfigureGoals();

  const isAccountFreeTier =
    auth?.user?.workspace?.billing?.plan === 'free_tier';
  const [alignmentDisableOpen, setAlignmentDisableOpen] = useState(false);
  const [
    freeTierFeature,
    setFreeTierFeature,
  ] = useState<FreeTierFeature | null>(null);

  const freeTierModal = useMemo(() => {
    const content = {
      objectiveName: {
        image: illustrationList.painting,
        title: 'Customize your goal terminology',
        description:
          'Replace Objectives and Key Results with language that matches how your team works.',
      },
      kpi: {
        image: illustrationList.createGoal,
        title: 'Use KPIs to measure leading indicators',
        description:
          'Enable KPI goals to set clear expectations and monitor progress alongside OKRs.',
      },
      goalAutoEnd: {
        image: illustrationList.cleanup,
        title: 'Automatically close goals after their due date',
        description:
          'Keep your workspace organized by automatically ending goals when they become overdue.',
      },
      allowGoalUpdate: {
        image: illustrationList.goal_ownership,
        title: 'Give members ownership of their goals',
        description:
          'Allow members to update the goals they create while managers and admins retain correction access.',
      },
    };

    return freeTierFeature ? content[freeTierFeature] : null;
  }, [freeTierFeature]);

  const withFreeTierGate = (feature: FreeTierFeature, action: () => void) => {
    if (isAccountFreeTier) {
      setFreeTierFeature(feature);
      return;
    }
    action();
  };

  const handleAlignmentChange = () => {
    if (alignmentEnabled) {
      setAlignmentDisableOpen(true);
      return;
    }
    setAlignment(true);
  };

  return (
    <>
      <GoalSettingsPage>
        <GoalSettingsIntro>
          <h1>Configure Goals</h1>
          <p>
            Manage goal settings, features, and permissions across your
            workspace.
          </p>
        </GoalSettingsIntro>

        <GoalSettingsBody>
          <SettingsCard>
            <SettingsCardTitle>Display</SettingsCardTitle>
            <DisplayNameRow
              field="goalName"
              title="Objectives display name"
              description="The label used for top-level goals throughout the workspace."
              value={draftNames.goalName}
              savedValue={savedNames.goalName}
              isLocked={isAccountFreeTier}
              limited={Boolean(featureLimitStatus?.limited)}
              isSaving={savingName === 'goalName'}
              onChange={(value) => updateDraftName('goalName', value)}
              onCancel={() => cancelDisplayName('goalName')}
              onSave={() => saveDisplayName('goalName')}
              onReset={() => stageDefaultDisplayName('goalName')}
              onLockedAction={() => setFreeTierFeature('objectiveName')}
            />
            <DisplayNameRow
              field="keyResultName"
              title="Key Result display name"
              description="The label used for key results and sub-goals throughout the workspace."
              value={draftNames.keyResultName}
              savedValue={savedNames.keyResultName}
              isLocked={isAccountFreeTier}
              limited={Boolean(featureLimitStatus?.limited)}
              isSaving={savingName === 'keyResultName'}
              onChange={(value) => updateDraftName('keyResultName', value)}
              onCancel={() => cancelDisplayName('keyResultName')}
              onSave={() => saveDisplayName('keyResultName')}
              onReset={() => stageDefaultDisplayName('keyResultName')}
              onLockedAction={() => setFreeTierFeature('objectiveName')}
            />
          </SettingsCard>

          <SettingsCard>
            <SettingsCardTitle>Features</SettingsCardTitle>
            <ToggleRow
              title="KPIs"
              description="Enable KPI-type goals alongside your OKRs. KPIs track a single metric without the key-result structure."
              checked={kpiEnabled}
              locked={isAccountFreeTier}
              disabled={busyFeature === 'kpi'}
              switchId={701}
              onChange={() =>
                withFreeTierGate('kpi', () => setKpi(!kpiEnabled))
              }
            />
            <ToggleRow
              title="Goal alignment and key results cascading"
              description="Enable goal hierarchies and let outcomes flow from parent to child goals. Disabling this removes all existing alignment links."
              checked={alignmentEnabled}
              disabled={busyFeature === 'alignment'}
              switchId={702}
              onChange={handleAlignmentChange}
            />
            <ToggleRow
              title="Goal auto-end"
              description="Automatically close goals that have passed their end date without a manual status update."
              checked={autoEndEnabled}
              locked={isAccountFreeTier}
              disabled={busyFeature === 'autoEnd'}
              switchId={703}
              onChange={() =>
                withFreeTierGate('goalAutoEnd', () =>
                  setAutoEnd(!autoEndEnabled),
                )
              }
            />
          </SettingsCard>

          <SettingsCard>
            <SettingsCardTitle>Permissions</SettingsCardTitle>
            <ToggleRow
              title="Members can edit their own goals"
              description="When off, members can view their goals but cannot change targets, status, or details. Managers and admins can always make corrections."
              checked={memberEditEnabled}
              locked={isAccountFreeTier}
              disabled={busyFeature === 'memberEdit'}
              switchId={704}
              onChange={() =>
                withFreeTierGate('allowGoalUpdate', () =>
                  setMemberEdit(!memberEditEnabled),
                )
              }
            />
          </SettingsCard>
        </GoalSettingsBody>
      </GoalSettingsPage>

      {freeTierModal && (
        <FreeTierModal
          open={Boolean(freeTierFeature)}
          image={freeTierModal.image}
          title={freeTierModal.title}
          description={freeTierModal.description}
          onClose={() => setFreeTierFeature(null)}
        />
      )}

      <Modal
        open={alignmentDisableOpen}
        onClose={() => setAlignmentDisableOpen(false)}>
        <ConfirmationDialog
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="disable-alignment-title">
          <ConfirmationHeader>
            <h2 id="disable-alignment-title">
              Disable goal alignment and key result cascading?
            </h2>
          </ConfirmationHeader>
          <ConfirmationBody>
            <p>
              This will remove existing goal alignment links and turn off
              alignment controls across the workspace. Please be certain before
              continuing.
            </p>
          </ConfirmationBody>
          <ConfirmationFooter>
            <Button
              type="button"
              kind="secondary"
              onClick={() => setAlignmentDisableOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busyFeature === 'alignment'}
              isLoading={busyFeature === 'alignment'}
              onClick={async () => {
                const updated = await setAlignment(false);
                if (updated) setAlignmentDisableOpen(false);
              }}>
              Disable alignment
            </Button>
          </ConfirmationFooter>
        </ConfirmationDialog>
      </Modal>
    </>
  );
});
